import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BarChart3 } from 'lucide-react'
import { adminApi, type ProductMetric } from '@/api/admin'
import { PageHeader } from '@/components/PageHeader'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { cn } from '@/components/ui/cn'
import { copy } from '@/lib/copy'
import { formatMetricValue } from '@/lib/metricFormat'

const PERIOD_OPTIONS = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
] as const

const PERIOD_AWARE_SECTIONS = new Set(['engagement', 'completion', 'economics'])

function MetricCard({ metric }: { metric: ProductMetric }) {
  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <BarChart3 className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{metric.label}</p>
          <p className="mt-1 text-3xl font-bold text-foreground" data-testid={`metric-value-${metric.key}`}>
            {formatMetricValue(metric)}
          </p>
          {metric.description ? (
            <p className="mt-2 text-sm text-muted-foreground" data-testid={`metric-desc-${metric.key}`}>
              {metric.description}
            </p>
          ) : null}
          {metric.cohortSize != null ? (
            <p className="mt-1 text-xs text-muted-foreground/70">Cohort: {metric.cohortSize}</p>
          ) : null}
        </div>
      </div>
    </Card>
  )
}

function CatalogRow({ metrics }: { metrics: ProductMetric[] }) {
  return (
    <Card className="p-5">
      <div className="grid gap-4 sm:grid-cols-3">
        {metrics.map((metric) => (
          <div key={metric.key} data-testid={`metric-value-${metric.key}`}>
            <p className="text-sm text-muted-foreground">{metric.label}</p>
            <p className="mt-1 text-2xl font-bold text-foreground">{formatMetricValue(metric)}</p>
            {metric.description ? (
              <p className="mt-1 text-xs text-muted-foreground">{metric.description}</p>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  )
}

function PeriodSelector({
  periodDays,
  onChange,
}: {
  periodDays: number
  onChange: (days: number) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="metrics-period-selector">
      <span className="text-sm text-muted-foreground">{copy.admin.metricsPeriodLabel}</span>
      <div className="inline-flex rounded-lg border border-border bg-muted/30 p-1">
        {PERIOD_OPTIONS.map((option) => (
          <button
            key={option.days}
            type="button"
            data-testid={`metrics-period-${option.days}`}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              periodDays === option.days
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => onChange(option.days)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export function AdminMetricsPage() {
  const [periodDays, setPeriodDays] = useState(7)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['adminMetrics', periodDays],
    queryFn: () => adminApi.getMetrics(periodDays),
  })

  return (
    <div data-testid="admin-metrics">
      <PageHeader title={copy.admin.metricsTitle} description={copy.admin.metricsDescription} />

      <div className="mb-6 space-y-2">
        <PeriodSelector periodDays={periodDays} onChange={setPeriodDays} />
        <p className="text-xs text-muted-foreground">{copy.admin.metricsPeriodHint}</p>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : null}

      {isError ? (
        <p className="text-sm text-destructive">{copy.admin.metricsLoadFailed}</p>
      ) : null}

      {data ? (
        <div className="space-y-8">
          {(data.sections ?? []).map((section) => (
            <section key={section.category} data-testid={`metrics-section-${section.category}`}>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {PERIOD_AWARE_SECTIONS.has(section.category)
                  ? section.title
                  : copy.admin.metricsSections[section.category as keyof typeof copy.admin.metricsSections]
                    ?? section.title}
              </h2>
              {section.category === 'catalog' ? (
                <CatalogRow metrics={section.metrics} />
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {section.metrics.map((metric) => (
                    <MetricCard key={metric.key} metric={metric} />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      ) : null}
    </div>
  )
}
